import { Tdu } from "./Tdu.js";
import { TduIdentifier } from "./TduIdentifier.js";
import { TduClass } from "./TduClass.js";
import { ParsedTdu } from "./ParsedTdu.js";
import * as D from "./DataDeserializer.js";
import { Decimal128 } from "./Decimal128.js";
import { Uuid } from "./Uuid.js";
import { merge } from "./DC.js";
import { Tru, TruComposite, type RemoteTypeDefResolver } from "./Tru.js";
import { TruIdentifier } from "./TruIdentifier.js";
import { TypedList, TypedMap, TypedTuple, tupleIdentifier } from "./descriptors.js";
import { IndexedStructure } from "./IndexedStructure.js";
import { TypeDefInfo } from "./types/TypeDefInfo.js";
import {
  GroupInt16Codec,
  GroupInt32Codec,
  GroupInt64Codec,
  GroupUInt16Codec,
  GroupUInt32Codec,
  GroupUInt64Codec,
} from "./gvwie/index.js";
import {
  Int8,
  UInt8,
  Int16,
  UInt16,
  Int32,
  UInt32,
  Int64,
  UInt64,
  Float32,
  Char16,
} from "./widths.js";
import * as S from "./DataSerializer.js";

/**
 * Self-describing value codec (port of C# `Codec`). Encodes a value to its
 * type-prefixed TDU representation.
 *
 * Type mapping for bare JS values:
 *  - `null`/`undefined` → Null
 *  - `boolean` → True/False
 *  - `number` → C# `double` semantics (narrowed; integral → smallest int)
 *  - `bigint` → C# `long` semantics (narrowed)
 *  - `string` → String
 *  - `Date` → DateTime, `Uuid` → UUID, `Decimal128` → Decimal128
 *  - width wrappers (`u8`, `i32`, `f32`, …) pin an explicit wire width
 *
 * Collections, maps, records and resources require the type registry and
 * connection context and are added in the Phase 2 continuation.
 */
export function composeInternal(
  value: unknown,
  warehouse: unknown = null,
  connection: unknown = null,
): Tdu {
  if (value === null || value === undefined)
    return new Tdu(TduIdentifier.Null, null, 0);

  // Explicit width wrappers
  if (value instanceof UInt8) return S.uint8Composer(value.value);
  if (value instanceof Int8) return S.int8Composer(value.value);
  if (value instanceof UInt16) return S.uint16Composer(value.value);
  if (value instanceof Int16) return S.int16Composer(value.value);
  if (value instanceof UInt32) return S.uint32Composer(value.value);
  if (value instanceof Int32) return S.int32Composer(value.value);
  if (value instanceof UInt64) return S.uint64Composer(value.value);
  if (value instanceof Int64) return S.int64Composer(value.value);
  if (value instanceof Float32) return S.float32Composer(value.value);
  if (value instanceof Char16) return S.char16Composer(value.value);

  switch (typeof value) {
    case "boolean":
      return S.boolComposer(value);
    case "number":
      return S.float64Composer(value);
    case "bigint":
      return S.int64Composer(value);
    case "string":
      return S.stringComposer(value);
  }

  if (value instanceof Date) return S.dateTimeComposer(value);
  if (value instanceof Uuid) return S.uuidComposer(value);
  if (value instanceof Decimal128) return S.decimal128Composer(value);
  if (value instanceof Uint8Array) return S.rawDataComposer(value);
  if (value instanceof TypedList) return typedListComposer(value, warehouse, connection);
  if (value instanceof TypedMap) return typedMapComposer(value, warehouse, connection);
  if (value instanceof TypedTuple) return tupleComposer(value, warehouse, connection);
  if (value instanceof Map) return mapComposer(value, warehouse, connection);

  // A bare JS array becomes a dynamic, self-describing List (each element keeps
  // its own type tag). Typed Gvwie arrays are produced from explicit typed-list
  // values via the Tru family.
  if (Array.isArray(value))
    return listComposer(value, TduIdentifier.List, warehouse, connection);
  if (value instanceof Map)
    return mapComposer(value, warehouse, connection);

  // A `Tru` value composes into its own dedicated, metadata-free TDU slot; a
  // `TypeDefInfo` composes into its own dedicated slot wrapping a structure
  // payload; any other `IndexedStructure` composes as a plain sparse
  // `TypedMap<u8, dynamic>` (see `IndexedStructureCodec`).
  if (value instanceof Tru) return S.truComposer(value, warehouse, connection);
  if (value instanceof TypeDefInfo) return S.typeDefComposer(value, warehouse, connection);
  if (value instanceof IndexedStructure)
    return S.structureComposer(value, warehouse, connection);

  // Records, enums and other registered structured types are handled by the
  // resource layer through hooks, keeping `data/` independent of it.
  for (const fn of structuredComposers) {
    const tdu = fn(value, warehouse, connection);
    if (tdu) return tdu;
  }

  throw new Error(
    `Codec.compose: serialization for ${describe(value)} is not supported.`,
  );
}

/**
 * Hooks for composing registered structured types (records, enums). Installed by
 * the resource layer; each returns a {@link Tdu} for a recognized value or
 * undefined. They are tried in registration order.
 */
const structuredComposers: Array<
  (value: unknown, warehouse: unknown, connection: unknown) => Tdu | undefined
> = [];

/** Register a structured-type composer hook (called by the resource layer). */
export function registerComposer(
  fn: (value: unknown, warehouse: unknown, connection: unknown) => Tdu | undefined,
): void {
  structuredComposers.push(fn);
}

/** Encode a value to its self-describing TDU bytes (leading identifier included). */
export function compose(
  value: unknown,
  warehouse: unknown = null,
  connection: unknown = null,
): Uint8Array {
  return composeInternal(value, warehouse, connection).composed;
}

/**
 * Compose a self-describing array as a single Dynamic TDU. Consecutive items of
 * the same typed shape collapse to a TypeContinuation (matching C#
 * `DynamicArrayComposer`); primitives never match, so each is emitted in full.
 */
function listComposer(
  value: Iterable<unknown>,
  identifier: TduIdentifier,
  warehouse: unknown,
  connection: unknown,
): Tdu {
  const parts: Uint8Array[] = [];
  let previous: Tdu | null = null;

  for (const el of value) {
    const tdu = composeInternal(el, warehouse, connection);
    if (previous && tdu.matchType(previous)) {
      const d = tdu.composed.subarray(tdu.contentOffset);
      parts.push(new Tdu(TduIdentifier.TypeContinuation, d, d.length).composed);
    } else {
      parts.push(tdu.composed);
    }
    previous = tdu;
  }

  const content = merge(...parts);
  return new Tdu(identifier, content, content.length);
}

/** Compose a dynamic Map TDU as alternating key/value self-describing TDUs. */
function mapComposer(
  value: Map<unknown, unknown>,
  warehouse: unknown,
  connection: unknown,
): Tdu {
  const flat: unknown[] = [];
  for (const [key, item] of value.entries()) {
    flat.push(key, item);
  }
  return listComposer(flat, TduIdentifier.Map, warehouse, connection);
}

/** Compose an explicitly-typed array as a Typed TDU with `TypedList<element>`. */
function typedListComposer(value: TypedList, warehouse: unknown, connection: unknown): Tdu {
  const payload = typedArrayComposer(value.values, value.element, warehouse, connection);
  const metadata = new TruComposite(TruIdentifier.TypedList, false, [value.element]);
  return new Tdu(TduIdentifier.Typed, payload, payload.length, metadata, connection);
}

/**
 * Encode the elements of a typed array. Numeric element types use the compact
 * Gvwie group encoding; other element types fall back to a concatenation of
 * self-describing TDUs (matching C# `DataSerializer.TypedArrayComposer` for
 * primitive elements).
 */
function typedArrayComposer(
  values: readonly unknown[],
  element: Tru,
  warehouse: unknown,
  connection: unknown,
): Uint8Array {
  switch (element.identifier) {
    case TruIdentifier.Int16:
      return GroupInt16Codec.encode(values as number[]);
    case TruIdentifier.Int32:
      return GroupInt32Codec.encode(values as number[]);
    case TruIdentifier.Int64:
      return GroupInt64Codec.encode(values as bigint[]);
    case TruIdentifier.UInt16:
      return GroupUInt16Codec.encode(values as number[]);
    case TruIdentifier.UInt32:
      return GroupUInt32Codec.encode(values as number[]);
    case TruIdentifier.UInt64:
      return GroupUInt64Codec.encode(values as bigint[]);
    default: {
      const parts: Uint8Array[] = [];
      let previous: Tdu | null = null;
      for (const v of values) {
        const tdu = composeInternal(v, warehouse, connection);
        if (tdu.tduClass === TduClass.Typed && tdu.metadata && element.match(tdu.metadata as Tru)) {
          // Element's type matches the declared element type: strip its metadata.
          const d = tdu.composed.subarray(tdu.contentOffset);
          parts.push(new Tdu(TduIdentifier.TypeOfTarget, d, d.length).composed);
        } else if (previous && tdu.matchType(previous)) {
          const d = tdu.composed.subarray(tdu.contentOffset);
          parts.push(new Tdu(TduIdentifier.TypeContinuation, d, d.length).composed);
        } else {
          parts.push(tdu.composed);
        }
        previous = tdu;
      }
      return merge(...parts);
    }
  }
}

/** Compose an explicitly-typed map as a Typed TDU (`TypedMap<key,value>`). */
function typedMapComposer(value: TypedMap, warehouse: unknown, connection: unknown): Tdu {
  const keys = value.entries.map((e) => e[0]);
  const values = value.entries.map((e) => e[1]);
  const composedKeys = typedArrayComposer(keys, value.keyType, warehouse, connection);
  const composedValues = typedArrayComposer(values, value.valueType, warehouse, connection);

  const keysTdu = new Tdu(TduIdentifier.TypeOfTarget, composedKeys, composedKeys.length).composed;
  const valuesTdu = new Tdu(TduIdentifier.TypeOfTarget, composedValues, composedValues.length).composed;
  const all = merge(keysTdu, valuesTdu);

  const metadata = new TruComposite(TruIdentifier.TypedMap, false, [value.keyType, value.valueType]);
  return new Tdu(TduIdentifier.Typed, all, all.length, metadata, connection);
}

/** Compose an explicitly-typed tuple as a Typed TDU (`TupleN<...>`). */
function tupleComposer(value: TypedTuple, warehouse: unknown, connection: unknown): Tdu {
  const parts: Uint8Array[] = [];
  for (let i = 0; i < value.values.length; i++) {
    const target = value.elements[i];
    const tdu = composeInternal(value.values[i], warehouse, connection);
    if (tdu.tduClass === TduClass.Typed && tdu.metadata && target.match(tdu.metadata as Tru)) {
      const d = tdu.composed.subarray(tdu.contentOffset);
      parts.push(new Tdu(TduIdentifier.TypeOfTarget, d, d.length).composed);
    } else {
      parts.push(tdu.composed);
    }
  }
  const all = merge(...parts);
  const metadata = new TruComposite(
    tupleIdentifier(value.elements.length),
    false,
    value.elements as Tru[],
  );
  return new Tdu(TduIdentifier.Typed, all, all.length, metadata, connection);
}

function describe(value: unknown): string {
  if (Array.isArray(value)) return "array";
  const ctor = (value as { constructor?: { name?: string } })?.constructor?.name;
  return ctor ?? typeof value;
}

// ---- decode -----------------------------------------------------------------

/** Sync parser dispatch table, indexed `[exponent][index]` for Fixed TDUs. */
const fixedParsers: D.Parser[][] = [
  [D.nullParser, D.booleanFalseParser, D.booleanTrueParser, D.notModifiedParser, D.infinityParser],
  [D.uint8Parser, D.int8Parser, D.char8Parser, D.localResource8Parser, D.resource8Parser],
  [D.uint16Parser, D.int16Parser, D.char16Parser, D.localResource16Parser, D.resource16Parser],
  [D.uint32Parser, D.int32Parser, D.float32Parser, D.localResource32Parser, D.resource32Parser],
  [D.uint64Parser, D.int64Parser, D.float64Parser, D.dateTimeParser],
  [D.uint128Parser, D.int128Parser, D.decimal128Parser, D.uuidParser],
];

const dynamicParsers: D.Parser[] = [
  D.rawDataParser,
  D.stringParser,
  D.listParser,
  D.resourceListParser,
  D.recordListParser,
  D.resourceLinkParser,
  D.mapParser,
  D.mapListParser,
];

const typedParsers: D.Parser[] = [D.typedParser, D.typeDefInfoParser, D.truParser];

/** Dispatch an already-parsed TDU header to the matching value parser. */
export function parseSyncTdu(tdu: ParsedTdu, warehouse: unknown = null): unknown {
  switch (tdu.tduClass) {
    case TduClass.Fixed:
      return fixedParsers[tdu.exponent][tdu.index](tdu, warehouse);
    case TduClass.Dynamic:
      return dynamicParsers[tdu.index](tdu, warehouse);
    case TduClass.Typed:
      return typedParsers[tdu.index](tdu, warehouse);
    case TduClass.Extension:
      throw new Error("Extension TDUs are not supported.");
    default:
      throw new Error("Invalid TDU.");
  }
}

/** Decode one value at `offset`; returns the value and bytes consumed. */
export function parseSync(
  data: Uint8Array,
  offset = 0,
  warehouse: unknown = null,
): { value: unknown; length: number } {
  const tdu = ParsedTdu.parseSync(data, offset, data.length, warehouse);
  if (tdu.tduClass === TduClass.Invalid)
    throw new Error("DataType can't be parsed.");
  return { value: parseSyncTdu(tdu, warehouse), length: tdu.totalLength };
}

/** Decode one value at `offset` and return just the value. */
export function parse(data: Uint8Array, offset = 0, warehouse: unknown = null): unknown {
  return parseSync(data, offset, warehouse).value;
}

// ---- async decode -------------------------------------------------------
//
// Parallel to the sync pipeline above, needed only because a `Tru`/`TypeDef`
// value anywhere in a decoded tree may reference a not-yet-fetched remote
// TypeDef. Most callers (regular request/reply decoding) never need this —
// it exists for `RemoteTypeDef`'s new-format (`TduIdentifier.TypeDef`, 0x81)
// decode path, which commonly contains member value-types that reference
// other remote TypeDefs on the same connection.

const dynamicAsyncParsers: D.AsyncParser[] = [
  D.rawDataParserAsync,
  D.stringParserAsync,
  D.listParserAsync,
  D.resourceListParserAsync,
  D.recordListParserAsync,
  D.resourceLinkParserAsync,
  D.mapParserAsync,
  D.mapListParserAsync,
];

const typedAsyncParsers: D.AsyncParser[] = [
  D.typedParserAsync,
  D.typeDefInfoParserAsync,
  D.truParserAsync,
];

/** Async twin of {@link parseSyncTdu}. */
export async function parseAsyncTdu(
  tdu: ParsedTdu,
  warehouse: unknown,
  remoteResolver: RemoteTypeDefResolver | undefined,
  requestSequence: readonly number[] | null,
): Promise<unknown> {
  switch (tdu.tduClass) {
    case TduClass.Fixed:
      // Fixed-class values are always primitives; no async resolution is ever needed.
      return fixedParsers[tdu.exponent][tdu.index](tdu, warehouse);
    case TduClass.Dynamic:
      return dynamicAsyncParsers[tdu.index](tdu, warehouse, remoteResolver, requestSequence);
    case TduClass.Typed:
      return typedAsyncParsers[tdu.index](tdu, warehouse, remoteResolver, requestSequence);
    case TduClass.Extension:
      throw new Error("Extension TDUs are not supported.");
    default:
      throw new Error("Invalid TDU.");
  }
}

/** Async twin of {@link parseSync}. */
export async function parseAsync(
  data: Uint8Array,
  offset: number,
  warehouse: unknown,
  remoteResolver: RemoteTypeDefResolver | undefined,
  requestSequence: readonly number[] | null,
): Promise<{ value: unknown; length: number }> {
  const tdu = await ParsedTdu.parseAsync(
    data,
    offset,
    data.length,
    warehouse,
    remoteResolver,
    requestSequence,
  );
  if (tdu.tduClass === TduClass.Invalid) throw new Error("DataType can't be parsed.");
  return {
    value: await parseAsyncTdu(tdu, warehouse, remoteResolver, requestSequence),
    length: tdu.totalLength,
  };
}
